/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikHelpers } from 'formik';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    RequestForQuoteClient,
    InstrumentAndRequestStep,
    FormStepStatusDto,
    YesNo,
} from '../../api/web-api-client';
import { tokenRequest } from '../../authentication/authConfig';
import { WizardFormStepValues, WizardStepProps } from '../../components/forms/WizardForm/types';
import { discardChanges } from '../common/constants';
import { instrumentAndRequestSaveValidation, instrumentAndRequestSubmitValidation } from './validation';
import { AccountDetails } from '../../authentication/accountContext';
import { formatBannerTitle } from '../common/helperFunctions';
import { formatDateStringToUTC } from '../../utils';
import AppLogger from '../../instrumentation/AppLogger';

const loadInstrumentAndRequest = (id: string, accounts: AccountInfo[], instance: IPublicClientApplication) => async (
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new RequestForQuoteClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            const instrumentAndRequest = await client.getInstrumentAndRequest(id, abortSignal);
            const wizardStepValues: WizardFormStepValues<InstrumentAndRequestStep> = {
                stepValues: { ...instrumentAndRequest },
            };
            return wizardStepValues;
        } catch (error) {
            AppLogger.error('Failed to load instrument and request details', error as Error, { Id: id });
            throw Error(`Failed to load instrument and request details. Id: ${id}`);
        }
    } else {
        AppLogger.verbose('There are no accounts available to load instrument and request details', { Id: id, Accounts: accounts });
        throw Error(`There are no accounts available to load instrument and request details. Id: ${id}`);
    }
};

const saveStep = (
    id: string,
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    isComplete: boolean,
) => async (
    values: InstrumentAndRequestStep,
    isDirty: boolean,
    _: FormikHelpers<InstrumentAndRequestStep>,
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new RequestForQuoteClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            AppLogger.verbose(`Before saving instrument and request details. Id: ${id}`, { values, isComplete });
            let formStepValues: InstrumentAndRequestStep | undefined;
            if (values.preferredInstrumentOrArtefactAvailabilityDate) {
                // eslint-disable-next-line max-len
                AppLogger.verbose(`Formatting preferred availability date to UTC for instrument and request details. Id: ${id}`, { preferredInstrumentOrArtefactAvailabilityDate: values.preferredInstrumentOrArtefactAvailabilityDate });
                const updatedValues = {
                    ...values,
                    preferredInstrumentOrArtefactAvailabilityDate: formatDateStringToUTC(values.preferredInstrumentOrArtefactAvailabilityDate),
                };
                formStepValues = updatedValues;
            } else {
                formStepValues = values;
            }
            AppLogger.verbose(`Saving instrument and request details. Id: ${id}`, { values, isComplete });
            await client.saveInstrumentAndRequest(
                id,
                {
                    applicationId: id,
                    formStep: formStepValues,
                    isCompletingStep: isComplete,
                },
                abortSignal,
            );
        } catch (error) {
            AppLogger.error('Failed to save instrument and request details', error as Error, { Id: id });
            throw Error(`Failed to save instrument and request details. Id: ${id}`);
        }
    } else {
        AppLogger.verbose('There are no accounts available to save instrument and request details', { Id: id, Accounts: accounts });
        throw Error(`There are no accounts available to save instrument and request details. Id: ${id}`);
    }
};

// const getRedirectionLocationOnError = (id: string) => (errorCode: number, errorType: ErrorType) => '/not-found';

const instrumentAndRequestProps = (
    id: string,
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    accountDetails: AccountDetails,
    statuses: FormStepStatusDto[],
    bannerTitle: string,
)
: WizardStepProps<InstrumentAndRequestStep> => ({
    initialValues: {
    },
    stepStatuses: statuses,
    loadStepValues: loadInstrumentAndRequest(id, accounts, instance),
    location: '/instrument-and-request',
    title: 'Instrument and request',
    hidingFields: {
        serialNumber: (x) => x.hasSerialNumber !== YesNo.Yes,
    },
    validateHard: instrumentAndRequestSubmitValidation,
    validateSoft: instrumentAndRequestSaveValidation,
    onSaveAndExit: saveStep(id, accounts, instance, false),
    onSaveAndNext: saveStep(id, accounts, instance, true),
    bannerTitle,
    bannerRefTitle: `Ref ID: ${id}`,
    bannerSubTitle: formatBannerTitle(accountDetails),
    // getRedirectionLocationOnError: getRedirectionLocationOnError(id),
    discard: discardChanges,
});

export default instrumentAndRequestProps;
